import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../lib/api';
import { isLlmInferenceAvailable } from '../lib/aiCapability';
import { useAuth } from '../context/AuthContext';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Send, Sparkles } from 'lucide-react';

// Get client's timezone offset in minutes
const getTimezoneOffset = () => new Date().getTimezoneOffset();

// Mood labels for display
const MOOD_LABELS = {
  morning: 'Good morning!',
  lunch: 'Lunch time!',
  dinner: 'Dinner time!',
  sleep: 'Getting sleepy...'
};

// Mood icons/emoji
const MOOD_ICONS = {
  morning: '☕',
  lunch: '🍱',
  dinner: '🍽️',
  sleep: '🌙'
};

export default function TeacherAvatar() {
  const { isAuthenticated, user } = useAuth();
  const [avatarData, setAvatarData] = useState(null);
  const [personalizedData, setPersonalizedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [personalizing, setPersonalizing] = useState(false);
  const [feelingText, setFeelingText] = useState('');
  const [showPopover, setShowPopover] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  
  // Track the currently displayed image URL for fade transitions
  const [displayedImageUrl, setDisplayedImageUrl] = useState(null);
  const [nextImageUrl, setNextImageUrl] = useState(null);
  
  const fetchIntervalRef = useRef(null);

  // Fetch the current avatar based on time bucket
  const fetchCurrentAvatar = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const data = await api.getAvatarCurrent(getTimezoneOffset());
      setAvatarData(data);
      
      // If we don't have a personalized version, use the base avatar
      if (!personalizedData) {
        if (displayedImageUrl !== data.image_url) {
          triggerImageTransition(data.image_url);
        }
      }
    } catch (error) {
      console.error('Failed to fetch avatar:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, personalizedData, displayedImageUrl]);

  // Trigger smooth fade transition between images
  const triggerImageTransition = useCallback((newUrl) => {
    if (!displayedImageUrl) {
      // First load - no transition needed
      setDisplayedImageUrl(newUrl);
      return;
    }
    
    // Start transition
    setTransitioning(true);
    setNextImageUrl(newUrl);
    
    // After fade out, swap images
    setTimeout(() => {
      setDisplayedImageUrl(newUrl);
      setNextImageUrl(null);
      setTransitioning(false);
    }, 300); // Match CSS transition duration
  }, [displayedImageUrl]);

  // Submit feeling for personalized avatar
  const handleSubmitFeeling = async (e) => {
    e.preventDefault();
    if (!feelingText.trim() || personalizing) return;
    
    setPersonalizing(true);
    
    try {
      const data = await api.getAvatarFeeling(
        feelingText.trim(),
        avatarData?.mood || null,
        getTimezoneOffset()
      );
      
      setPersonalizedData(data);
      triggerImageTransition(data.image_url);
      setFeelingText('');
      setShowPopover(false);
    } catch (error) {
      console.error('Failed to get personalized avatar:', error);
      // Show error toast if available
    } finally {
      setPersonalizing(false);
    }
  };

  // Clear personalization and go back to base avatar
  const handleClearPersonalization = () => {
    setPersonalizedData(null);
    if (avatarData) {
      triggerImageTransition(avatarData.image_url);
    }
  };

  // Initial fetch and periodic refresh
  useEffect(() => {
    if (isAuthenticated) {
      fetchCurrentAvatar();
      
      // Refresh every 5 minutes to check for time bucket changes
      fetchIntervalRef.current = setInterval(fetchCurrentAvatar, 5 * 60 * 1000);
      
      return () => {
        if (fetchIntervalRef.current) {
          clearInterval(fetchIntervalRef.current);
        }
      };
    }
  }, [isAuthenticated, fetchCurrentAvatar]);

  // Don't render if not authenticated
  if (!isAuthenticated) return null;

  const currentMood = personalizedData?.mood || avatarData?.mood || 'morning';
  const currentImageUrl = displayedImageUrl || avatarData?.image_url;
  const isPersonalized = !!personalizedData;
  const hasApiKey = isLlmInferenceAvailable(user);

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <Popover open={showPopover} onOpenChange={setShowPopover}>
        <PopoverTrigger asChild>
          <button
            className="relative group focus:outline-none"
            aria-label="Teacher Avatar"
          >
            {/* Avatar container with glow effect */}
            <div className="relative">
              {/* Glow effect */}
              <div 
                className={`absolute inset-0 rounded-full blur-md transition-all duration-500 ${
                  isPersonalized 
                    ? 'bg-gradient-to-r from-pink-400/40 to-purple-400/40' 
                    : 'bg-gradient-to-r from-green-400/30 to-teal-400/30'
                } group-hover:blur-lg group-hover:scale-110`}
              />
              
              {/* Avatar image container */}
              <div 
                className={`relative w-20 h-20 rounded-full overflow-hidden border-2 transition-all duration-300 ${
                  isPersonalized 
                    ? 'border-pink-300 shadow-lg shadow-pink-200/50' 
                    : 'border-green-300 shadow-lg shadow-green-200/50'
                } group-hover:scale-105 bg-gradient-to-br from-green-50 to-teal-50`}
              >
                {loading ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-green-500" />
                  </div>
                ) : (
                  <>
                    {/* Current image with fade effect */}
                    <img
                      src={currentImageUrl}
                      alt="Teacher Avatar"
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
                        transitioning ? 'opacity-0' : 'opacity-100'
                      }`}
                      onLoad={() => setImageLoaded(true)}
                      onError={(e) => {
                        // Fallback to placeholder on error
                        e.target.src = `https://via.placeholder.com/256x256.png?text=${currentMood}`;
                      }}
                    />
                    
                    {/* Next image (for transition) */}
                    {nextImageUrl && (
                      <img
                        src={nextImageUrl}
                        alt="Teacher Avatar (loading)"
                        className="absolute inset-0 w-full h-full object-cover opacity-0"
                        onLoad={() => {
                          // Image is loaded, we can complete the transition
                        }}
                      />
                    )}
                  </>
                )}
                
                {/* Mood indicator badge */}
                <div className="absolute -bottom-1 -right-1 text-xl">
                  {MOOD_ICONS[currentMood]}
                </div>
              </div>
              
              {/* Personalization sparkle indicator */}
              {isPersonalized && (
                <div className="absolute -top-1 -right-1">
                  <Sparkles className="w-5 h-5 text-pink-400 animate-pulse" />
                </div>
              )}
            </div>
            
            {/* Hover hint */}
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
              <span className="bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-lg">
                {MOOD_LABELS[currentMood]}
              </span>
            </div>
          </button>
        </PopoverTrigger>
        
        <PopoverContent 
          side="top" 
          align="end" 
          className="w-72 p-4"
          sideOffset={12}
        >
          <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center gap-2">
              <span className="text-2xl">{MOOD_ICONS[currentMood]}</span>
              <div>
                <h4 className="font-medium text-sm">
                  {MOOD_LABELS[currentMood]}
                </h4>
                <p className="text-xs text-muted-foreground">
                  {isPersonalized 
                    ? `Feeling ${personalizedData.emotion_class}` 
                    : 'How are you feeling today?'}
                </p>
              </div>
            </div>
            
            {/* Feeling input */}
            {hasApiKey ? (
              <form onSubmit={handleSubmitFeeling} className="space-y-3">
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="I'm feeling..."
                    value={feelingText}
                    onChange={(e) => setFeelingText(e.target.value)}
                    className="pr-10 text-sm"
                    disabled={personalizing}
                    maxLength={100}
                  />
                  <Button
                    type="submit"
                    size="sm"
                    variant="ghost"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                    disabled={!feelingText.trim() || personalizing}
                  >
                    {personalizing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                
                {/* Quick emotion buttons */}
                <div className="flex flex-wrap gap-1">
                  {['Happy', 'Tired', 'Stressed', 'Motivated'].map((emotion) => (
                    <Button
                      key={emotion}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs h-6 px-2"
                      onClick={() => setFeelingText(`I'm feeling ${emotion.toLowerCase()}`)}
                      disabled={personalizing}
                    >
                      {emotion}
                    </Button>
                  ))}
                </div>
                
                {/* Clear personalization button */}
                {isPersonalized && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full text-xs text-muted-foreground"
                    onClick={handleClearPersonalization}
                  >
                    Reset to default avatar
                  </Button>
                )}
              </form>
            ) : (
              <p className="text-xs text-muted-foreground">
                Add your OpenAI API key in settings to personalize your avatar!
              </p>
            )}
            
            {/* Footer info */}
            <div className="text-xs text-muted-foreground border-t pt-2">
              <p>Your study companion changes throughout the day!</p>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
